import { getCurrentAccessToken, getCurrentUserName, getIsImpersonating } from "features/authSlice";
import NavBar from "components/ui/nav-bar/NavBar";
import { useSelector } from "react-redux";
import { Outlet, useLocation, matchPath } from "react-router-dom";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import nafhubImg from "assets/images/nafhub-light.svg";
import NewHeaderLogo from "assets/images/v2/Nafhub-new-poweredByNAF.png";
import "./header.scss";
import { getStepData } from "pages/application/question/questionSlice";
import useDesktopChecker from "hooks/useDesktopChecker";
import { PhoneNumber, CONTACT_US } from "utilities/Constant";
import GlobalHeader from "components/v2/global-header/GlobalHeader";
import { NotificationProvider } from "contexts/NotificationContext";
import PreferredLanguage from "components/v2/preferred-language";
import { useLoanCentricSidebarNavEnabled } from "hooks/useLoanCentricSidebarNavEnabled";

function Header() {
  const { pathname } = useLocation();
  const path = pathname.toLowerCase();
  const userName = useSelector(getCurrentUserName);
  const isImpersonating = useSelector(getIsImpersonating);
  const accessToken = useSelector(getCurrentAccessToken);
  const showNavLinks = pathname.includes("/dashboard");
  const isLos = pathname.includes("/los");
  const hideNotificationsForRoute = path === "/my-loans" || path === "/path" || !accessToken;
  const loanCentricSidebarNavEnabled = useLoanCentricSidebarNavEnabled();

  const noHeader =
    matchPath("/application/*", path) ||
    path === "/application" ||
    path === "/dashboard" ||
    matchPath("/home/*", path) ||
    path === "/home" ||
    (loanCentricSidebarNavEnabled && path === "/my-loans") ||
    path === "/post-auth";
  const oldHeader =
    path === "/" ||
    matchPath("/registration/*", path) ||
    path === "/registration" ||
    path === "/registrationcoborrower" ||
    path === "/mfa" ||
    path === "/forgotpassword" ||
    path === "/resetpassword" ||
    path === "/authcode" ||
    path === "/registrationauth" ||
    path === "/updatepassword" ||
    path === "/form/" ||
    matchPath("/external-registration/*", path);
  const newHeader = !noHeader && !oldHeader;
  return (
    <>
      {newHeader && (
        <NotificationProvider enabled={false}>
          <GlobalHeader showLogoOnDesktop hideNotifications={hideNotificationsForRoute} />
        </NotificationProvider>
      )}
      {oldHeader && <HeaderComponent userName={userName} showNavLinks={showNavLinks} isLos={isLos} isImpersonating={isImpersonating} />}
      <Outlet />
    </>
  );
}

interface HeaderComponentProps {
  readonly userName: string;
  readonly showNavLinks: boolean;
  readonly isLos?: boolean;
  readonly isImpersonating?: boolean;
}

export function HeaderComponent({ userName, showNavLinks, isLos, isImpersonating }: HeaderComponentProps) {
  const stepData = useSelector(getStepData);
  const summaryHeader = stepData?.stepType === "summary";
  const isDesktop = useDesktopChecker();
  const contactNumber = isLos ? CONTACT_US : PhoneNumber;
  const handleClick = () => {
    if (!isDesktop) {
      globalThis.location.href = `tel:${contactNumber}`;
    }
  };
  const pathName = useLocation().pathname.toLowerCase();
  const isDocument = location.pathname === "/dashboard/documents";
  const isHeaderWhite =
    location.pathname === "/resetpassword" ||
    location.pathname === "/forgotpassword" ||
    location.pathname === "/" ||
    location.pathname === "/mfa" ||
    location.pathname === "/authcode" ||
    pathName.includes("/registration") ||
    pathName.includes("/registrationCoBorrower") ||
    pathName.includes("/registrationAuth") ||
    pathName.includes("/updatepassword") ||
    pathName.includes("/start") ||
    pathName.includes("external-registration") ||
    pathName.includes("/form");

  let headerLogo;
  if (isHeaderWhite) {
    headerLogo = NewHeaderLogo;
  } else {
    headerLogo = nafhubImg;
  }

  let logoClassName;
  if (isHeaderWhite) {
    logoClassName = "nafhub-logo-v2";
  } else {
    logoClassName = "nafhub-logo";
  }

  return (
    <div className={isHeaderWhite ? "new-header" : "header-container"} style={isImpersonating ? { backgroundColor: "red" } : {}}>
      <header className={isDocument || summaryHeader ? "document-header" : "nafhub-header"} style={isImpersonating ? { backgroundColor: "red" } : {}}>
        <img className={logoClassName} src={headerLogo} alt="logo" />
        {!userName && !showNavLinks && (
          <div>
            {isDesktop ? (
              <div className="header-support-section">
                <div className="header-support-section__preferred-language" >
                  <PreferredLanguage isAuthenticated={false} /></div>
                <p className="nafhub-phonenumber">Need help? {contactNumber}</p>
              </div>
            ) : (
              <div className="header-support-section">
                <PreferredLanguage isMobile isAuthenticated={false}  />
                <a href={`tel:${contactNumber}`} onClick={handleClick} title="phone number">
                  <FontAwesomeIcon icon={solid("phone-flip")} className="nafhub-phonenumber" />
                </a>
              </div>
            )}
          </div>
        )}
        {userName && (
          <span className={showNavLinks ? "show-links" : "hide-links"}>
            <NavBar userName={userName} showNavLinks={showNavLinks} />
          </span>
        )}
      </header>
    </div>
  );
}

export default Header;
